import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import MobileAppBar from '@/components/molecules/MobileAppBar';
import WorkActivityPreview from '@/components/molecules/workActivity/WorkActivityPreview';
import WorkActivitySubmitterProfile from '@/components/molecules/workActivity/WorkActivitySubmitterProfile';
import WorkActivityRejectModal from '@/components/molecules/workActivity/WorkActivityRejectModal';
import ConfirmModal from '@/components/molecules/ConfirmModal';
import InfoModal from '@/components/molecules/InfoModal';
import WorkActivityAllocationsDetails from '@/components/organisms/workActivity/WorkActivityAllocationsDetails';
import WorkActivityDocuments from '@/components/organisms/workActivity/WorkActivityDocuments';
import InvoiceInfo from '@/components/organisms/invoice/InvoiceInfo';
import { Button } from '@/components/ui/button';
import { budgetService } from '@/services/budget.service';
import { workActivityService } from '@/services/work-activity.service';
import { WorkActivity } from '@/services/types/work-activity.types';
import { BudgetMemberEntry } from '@/services/types/budgets.types';
import { PAGE } from '@/routes';
import {
  formatWorkActivityDurationLabel,
  formatWorkActivityTableAmount,
  getWorkActivityStatusLabel
} from '@/utils/workActivityForm';
import { useBudgetPermissions } from '@/hooks/useBudgetPermissions';
import { getCurrentUser } from '@/store/slices/auth';
import { getSubmitterHourlyRate, isWorkActivityCreator } from '@/utils/workActivityHelpers';
import workActivityPendingIcon from '@/assets/icons/work-activity-pending.svg';
import workActivityRejectedIcon from '@/assets/icons/work-activity-rejected.svg';
import workActivityAcceptedIcon from '@/assets/icons/work-activity-accepted.svg';
import workActivityApproveIcon from '@/assets/icons/work-activity-approve.svg';
import { ReactComponent as EditIcon } from '@/assets/icons/edit-purple.svg';
import { ReactComponent as DeleteIcon } from '@/assets/icons/bin.svg';
import { ReactComponent as DeleteInvoiceIcon } from '@/assets/icons/delete-invoice.svg';

const STATUS_ICONS = {
  PENDING: workActivityPendingIcon,
  REJECTED: workActivityRejectedIcon,
  APPROVED: workActivityAcceptedIcon
};

const WorkActivityDetail = () => {
  const { budgetId, workActivityId } = useParams<{ budgetId: string; workActivityId: string }>();
  const navigate = useNavigate();

  const [workActivity, setWorkActivity] = useState<WorkActivity | null>(null);
  const [budgetMembers, setBudgetMembers] = useState<BudgetMemberEntry[]>([]);
  const [currency, setCurrency] = useState('EUR');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'information' | 'allocation' | 'document'>('information');
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [approveConfirmModalOpen, setApproveConfirmModalOpen] = useState(false);
  const [approveSuccessModalOpen, setApproveSuccessModalOpen] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [loadingApprove, setLoadingApprove] = useState(false);
  const [loadingReject, setLoadingReject] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);

  const currentUser = useSelector(getCurrentUser);
  const { policies, member } = useBudgetPermissions({
    budgetId,
    members: budgetMembers
  });

  const canValidate = useMemo(() => policies.canValidateWorkActivity(workActivity), [policies, workActivity]);

  const isCreator = useMemo(
    () => isWorkActivityCreator(workActivity, member, currentUser),
    [workActivity, member, currentUser]
  );

  const canDelete = isCreator;
  const canEdit = isCreator && workActivity?.status === 'PENDING';
  const canSubmitRevision = isCreator && workActivity?.status === 'REJECTED';

  const fetchWorkActivityDetail = async (showLoader = false) => {
    if (!budgetId || !workActivityId) {
      return;
    }

    if (showLoader) {
      setLoading(true);
    }

    try {
      const [activity, budgetResponse] = await Promise.all([
        workActivityService.getWorkActivity(budgetId, workActivityId),
        budgetService.getBudget(budgetId)
      ]);
      setWorkActivity(activity);
      setBudgetMembers(budgetResponse.data.members ?? []);
      setCurrency(budgetResponse.data.currency ?? 'EUR');
    } catch (error: any) {
      console.error('Error fetching work activity detail:', error);
      toast.error(error?.message ?? 'Failed to load work activity details.');
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchWorkActivityDetail(true);
  }, [budgetId, workActivityId]);

  const handleApprove = async () => {
    if (!budgetId || !workActivityId) {
      return;
    }

    try {
      setLoadingApprove(true);
      await workActivityService.approveWorkActivity(budgetId, workActivityId);
      setApproveConfirmModalOpen(false);
      setApproveSuccessModalOpen(true);
    } catch (error: any) {
      console.error('Error approving work activity:', error);
      toast.error(error?.message ?? 'Failed to approve work activity.');
    } finally {
      setLoadingApprove(false);
    }
  };

  const handleApproveSuccessComplete = async () => {
    setApproveSuccessModalOpen(false);
    await fetchWorkActivityDetail();
  };

  const handleReject = async (rejectionNote: string) => {
    if (!budgetId || !workActivityId) {
      return;
    }

    try {
      setLoadingReject(true);
      await workActivityService.rejectWorkActivity(budgetId, workActivityId, rejectionNote);
      toast.success('Work activity rejected.');
      setRejectModalOpen(false);
      navigate(`${PAGE.BUDGETS.path}/${budgetId}`);
    } catch (error: any) {
      console.error('Error rejecting work activity:', error);
      toast.error(error?.message ?? 'Failed to reject work activity.');
    } finally {
      setLoadingReject(false);
    }
  };

  const handleDelete = async () => {
    if (!budgetId || !workActivityId) {
      return;
    }

    try {
      setLoadingDelete(true);
      await workActivityService.deleteWorkActivity(budgetId, workActivityId);
      toast.success('Work activity deleted successfully');
      setDeleteConfirmModal(false);
      navigate(`${PAGE.BUDGETS.path}/${budgetId}`);
    } catch (error: any) {
      console.error('Error deleting work activity:', error);
      toast.error(error?.message ?? 'Failed to delete work activity.');
      setLoadingDelete(false);
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleEdit = () => {
    if (!budgetId || !workActivityId) {
      return;
    }
    navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${workActivityId}/edit`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full flex-col gap-2">
        <Loader2 className="w-6 h-6 animate-spin" />
        <span className="text-sm font-medium">Loading work activity details...</span>
      </div>
    );
  }

  if (!workActivity) {
    return (
      <div className="flex items-center justify-center h-full flex-col gap-2">
        <span className="text-sm font-medium text-secondary">Work activity not found.</span>
      </div>
    );
  }

  const amount = workActivity.total_amount ?? null;
  const submitterHourlyRate = getSubmitterHourlyRate(workActivity, budgetMembers);

  const infoItems = [
    {
      label: 'Activity',
      value: workActivity.activity,
      type: 'text' as const,
      span: 1
    },
    {
      label: 'Description',
      value: workActivity.description?.trim() ? workActivity.description : '-',
      type: 'text' as const,
      span: 1
    },
    {
      label: 'Activity date',
      value: workActivity.activity_date ? format(new Date(workActivity.activity_date), 'd MMMM yyyy') : '-',
      type: 'text' as const,
      span: 1
    },
    {
      label: 'Duration',
      value: formatWorkActivityDurationLabel(workActivity.duration_hours, workActivity.duration_minutes),
      type: 'text' as const,
      span: 1
    },
    {
      label: 'Amount',
      value: formatWorkActivityTableAmount(amount, currency),
      type: 'text' as const,
      span: 1
    },
    {
      label: 'Status',
      value: (
        <div className="flex items-center gap-1">
          <img src={STATUS_ICONS[workActivity.status]} alt="" className="w-4 h-4" />
          <span>{getWorkActivityStatusLabel(workActivity.status)}</span>
        </div>
      ),
      type: 'text' as const,
      span: 1
    },
    ...(workActivity.rejection_note
      ? [
          {
            label: 'Rejection Notes from Validator',
            value: workActivity.rejection_note,
            type: 'text' as const,
            span: 1
          }
        ]
      : [])
  ];

  return (
    <>
      <MobileAppBar
        title={<span className="capitalize">Work Activity Detail</span>}
        onRightIconClick={canEdit ? handleEdit : undefined}
        rightIcon={canEdit ? <EditIcon className="w-[18px] h-[18px] text-dark" /> : undefined}
        rightTwoIcon={canDelete ? <DeleteIcon className="w-5 h-5" /> : undefined}
        onRightTwoIconClick={canDelete ? () => setDeleteConfirmModal(true) : undefined}
      />

      <div className="flex-1 p-4 md:p-0 w-full max-w-[1440px] mx-auto flex flex-col md:flex-row gap-6 justify-between">
        <aside className="md:w-[330px] pt-12 space-y-6 flex flex-col">
          <WorkActivityPreview workActivity={workActivity} currency={currency} />

          <WorkActivitySubmitterProfile
            name={workActivity.creator.name}
            profilePicUrl={workActivity.creator.profile_pic_url}
            hourlyRate={submitterHourlyRate}
            currency={currency}
          />

          {canSubmitRevision && budgetId && workActivityId && (
            <Button
              type="button"
              variant="primary-reverse"
              className="rounded-full w-full text-base font-semibold"
              onClick={() =>
                navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${workActivityId}/submit-revision`)
              }
            >
              Submit Revision
            </Button>
          )}

          {canValidate && (
            <div className="hidden md:flex flex-col gap-2.5 mt-9">
              <Button
                type="button"
                variant="primary-reverse"
                className="rounded-full w-full text-base font-semibold bg-[#E8507E] text-white hover:bg-[#E8507E]/90 border-[#E8507E]"
                onClick={() => setRejectModalOpen(true)}
                disabled={loadingApprove || loadingReject}
              >
                Reject
              </Button>
              <Button
                type="button"
                variant="default"
                className="rounded-full w-full text-base font-semibold"
                onClick={() => setApproveConfirmModalOpen(true)}
                disabled={loadingApprove || loadingReject}
              >
                Approve
              </Button>
              <p className="text-secondary text-xs md:text-sm font-medium text-center">
                After you approved, it will be finalized and consume your Budget Total Expense Value.
              </p>
            </div>
          )}
        </aside>

        <div className="p-2 flex-1 flex flex-col gap-4">
          <div className="flex border-b items-center gap-4">
            <button
              type="button"
              className={`text-sm md:text-lg font-normal px-0 py-1 md:py-2 border-b-2 ${activeTab === 'information' ? 'border-primary font-medium' : 'border-transparent'}`}
              onClick={() => setActiveTab('information')}
            >
              Information
            </button>
            <button
              type="button"
              className={`text-sm md:text-lg font-normal px-0 py-1 md:py-2 border-b-2 ${activeTab === 'allocation' ? 'border-primary font-medium' : 'border-transparent'}`}
              onClick={() => setActiveTab('allocation')}
            >
              Allocation
            </button>
            <button
              type="button"
              className={`text-sm md:text-lg font-normal px-0 py-1 md:py-2 border-b-2 ${activeTab === 'document' ? 'border-primary font-medium' : 'border-transparent'}`}
              onClick={() => setActiveTab('document')}
            >
              Document
            </button>
          </div>

          <div className="flex-1 flex flex-col md:border md:bg-white md:p-4 md:rounded-[16px]">
            <div className="flex-1">
              {activeTab === 'information' && <InvoiceInfo items={infoItems} />}
              {activeTab === 'allocation' && <WorkActivityAllocationsDetails workActivity={workActivity} />}
              {activeTab === 'document' && <WorkActivityDocuments documents={workActivity.documents_url ?? []} />}
            </div>

            {(canDelete || canEdit) && (
              <footer className="hidden md:flex items-center justify-end gap-2 mt-4">
                {canEdit && (
                  <Button
                    type="button"
                    variant="text"
                    className="flex items-center justify-center gap-2 w-fit rounded-full text-base font-semibold"
                    onClick={handleEdit}
                  >
                    <EditIcon className="w-5 h-5" />
                    <span>Edit Work Activity</span>
                  </Button>
                )}
                {canDelete && (
                  <Button
                    type="button"
                    variant="text"
                    className="text-[#E8507E] flex items-center justify-center gap-2 w-fit text-base font-semibold"
                    onClick={() => setDeleteConfirmModal(true)}
                    disabled={loadingDelete}
                  >
                    <DeleteIcon className="w-5 h-5" />
                    <span>Delete Work Activity</span>
                  </Button>
                )}
              </footer>
            )}
          </div>

          {canSubmitRevision && budgetId && workActivityId && (
            <div className="md:hidden">
              <Button
                type="button"
                variant="primary-reverse"
                className="rounded-full w-full text-base font-semibold"
                onClick={() =>
                  navigate(`${PAGE.BUDGETS.path}/${budgetId}/work-activity/${workActivityId}/submit-revision`)
                }
              >
                Submit Revision
              </Button>
            </div>
          )}

          {canValidate && (
            <div className="md:hidden flex flex-col gap-2.5">
              <Button
                type="button"
                variant="primary-reverse"
                className="rounded-full w-full text-base font-semibold bg-[#E8507E] text-white hover:bg-[#E8507E]/90 border-[#E8507E]"
                onClick={() => setRejectModalOpen(true)}
                disabled={loadingApprove || loadingReject}
              >
                Reject
              </Button>
              <Button
                type="button"
                variant="default"
                className="rounded-full w-full text-base font-semibold"
                onClick={() => setApproveConfirmModalOpen(true)}
                disabled={loadingApprove || loadingReject}
              >
                Approve
              </Button>
              <p className="text-secondary text-xs font-medium text-center">
                After you approved, it will be finalized and consume your Budget Total Expense Value.
              </p>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        isOpen={approveConfirmModalOpen}
        title="Are you sure want to approve this work activity?"
        message="Once approved, your Budget Total Expense Value will decreased."
        buttonText={loadingApprove ? 'Approving...' : 'Approve'}
        onConfirm={handleApprove}
        onClose={() => setApproveConfirmModalOpen(false)}
        icon={workActivityApproveIcon}
        loading={loadingApprove}
      />

      <InfoModal
        isOpen={approveSuccessModalOpen}
        variant="success"
        title="Successfully Approve Work Activity"
        message="Your employee will be notified"
        buttonText="Complete"
        onButtonClick={handleApproveSuccessComplete}
        icon=""
      />

      {deleteConfirmModal && (
        <InfoModal
          isOpen={deleteConfirmModal}
          variant="confirm"
          title="Are you sure want to Delete this Work Activity?"
          message={<p>Deleted work activity will be removed permanently and unable to restore</p>}
          buttonText="Delete"
          onButtonClick={handleDelete}
          onClose={() => setDeleteConfirmModal(false)}
          icon={<DeleteInvoiceIcon />}
          loading={loadingDelete}
          loadingButtonText="Deleting..."
        />
      )}

      <WorkActivityRejectModal
        isOpen={rejectModalOpen}
        onClose={() => setRejectModalOpen(false)}
        onConfirm={handleReject}
        loading={loadingReject}
      />
    </>
  );
};

export default WorkActivityDetail;
